import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FiArrowLeft,
  FiBarChart,
  FiCheck,
  FiClock,
  FiCopy,
  FiDatabase,
  FiEye,
  FiLayers,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTarget
} from 'react-icons/fi';
import { labelTokenizerPlatformCategoryId, TOKENIZER_PLATFORM_SECTORS } from '../../api/smartTokenizerMappers';
import { filterAssetClasses } from '../../utils/filterAssetClasses';
import { hasAssetClassDeployedContract } from '../../utils/assetClassContractUiAccess';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { useAssetClass } from '../../contexts/AssetClassContext';
import AssetClassCreationWizard from '../AssetClassManager/AssetClassCreationWizard';
import BatchMintingWizard from '../AssetClassManager/BatchMintingWizard';
import AssetClassDetailsModal from '../AssetClassManager/AssetClassDetailsModal';
import AssetClassDeployContractModal from '../AssetClassManager/AssetClassDeployContractModal';
import AssetClassCustodyVaultDeployModal from '../AssetClassManager/AssetClassCustodyVaultDeployModal';
import AssetClassFractionTokenDeployModal from '../AssetClassManager/AssetClassFractionTokenDeployModal';
import { AssetClassContractCardBlock } from '../AssetClassManager/AssetClassContractCardBlock';
import { AssetClassCustodyVaultBlock } from '../AssetClassManager/AssetClassCustodyVaultBlock';
import { AssetClassFractionTokenBlock } from '../AssetClassManager/AssetClassFractionTokenBlock';

const ManagerContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #9bd6ff;
  text-decoration: none;
  margin-bottom: 1.5rem;
  font-weight: 500;
  &:hover {
    color: #c4e4ff;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.05rem;
  color: #a0a0a0;
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 1rem;
  .icon {
    color: #4facfe;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${(props) =>
    props.primary ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'rgba(255, 255, 255, 0.1)'};
  color: #fff;
  border: ${(props) => (props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)')};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    transform: translateY(-2px);
    background: ${(props) =>
      props.primary ? 'linear-gradient(135deg, #3d8bfe, #00d4fe)' : 'rgba(255, 255, 255, 0.2)'};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const FilterSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 200px;
  max-width: 420px;
  color: #94a3b8;
  svg {
    flex-shrink: 0;
  }
`;

const FilterTextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0.75rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &::placeholder {
    color: #64748b;
  }
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterSectorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  label {
    color: #94a3b8;
    font-size: 0.9rem;
    white-space: nowrap;
  }
`;

const FilterSelect = styled.select`
  padding: 0.6rem 0.75rem;
  min-width: 200px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterMeta = styled.span`
  font-size: 0.85rem;
  color: #64748b;
  @media (min-width: 720px) {
    margin-left: auto;
  }
`;

const AssetClassesSection = styled.div`
  margin-bottom: 3rem;
`;

const AssetClassGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const AssetClassCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-3px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.1);
  }
  .class-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1.5rem;
  }
  .class-info {
    flex: 1;
  }
  .class-name {
    font-size: 1.4rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }
  .class-details {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
  .badge-standard {
    display: inline-block;
    margin-top: 0.35rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #c4b5fd;
    border: 1px solid rgba(196, 181, 253, 0.45);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
  }
  .class-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
  }
  .metric {
    text-align: center;
    .metric-value {
      font-size: 1.2rem;
      font-weight: 600;
      color: #4facfe;
      margin-bottom: 0.25rem;
    }
    .metric-label {
      color: #a0a0a0;
      font-size: 0.8rem;
    }
  }
  .class-actions {
    display: flex;
    gap: 0.75rem;
  }
  .class-action {
    flex: 1;
    padding: 0.75rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 8px;
    color: #4facfe;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    &:hover {
      background: rgba(79, 172, 254, 0.2);
    }
    &.primary {
      background: linear-gradient(135deg, #4facfe, #00f2fe);
      color: #fff;
      border: none;
      &:hover {
        background: linear-gradient(135deg, #3d8bfe, #00d4fe);
      }
      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        background: rgba(79, 172, 254, 0.22);
        color: rgba(255, 255, 255, 0.75);
      }
      &:disabled:hover {
        background: rgba(79, 172, 254, 0.22);
        transform: none;
      }
    }
  }
`;

const Gate = styled.div`
  text-align: center;
  padding: 3rem;
  color: #fecaca;
  background: rgba(255, 80, 80, 0.08);
  border-radius: 16px;
  border: 1px solid rgba(255, 100, 100, 0.25);
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const FormModalSurface = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 520px;
  width: 100%;
  padding: 1.75rem;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  color: #94a3b8;
  font-size: 0.9rem;
  span {
    color: #cbd5e1;
  }
  input,
  select {
    padding: 0.65rem 0.75rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(0, 0, 0, 0.25);
    color: #fff;
    font-size: 0.95rem;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const SchemaViewModalSurface = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  padding: 1.5rem;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);
`;

const SchemaModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  h2 {
    color: #fff;
    font-size: 1.25rem;
    margin: 0;
  }
  .meta {
    color: #94a3b8;
    font-size: 0.85rem;
    margin-top: 0.35rem;
  }
`;

function AdminAssetClasses() {
  const { isAdmin } = useTokenIssuer();
  const {
    assetClasses,
    assetClassTemplates,
    createFromTemplate,
    loadAssetClasses,
    getMarketPairForAssetClass,
    loading,
  } = useAssetClass();

  const [activeModal, setActiveModal] = useState(null);
  const [selectedAssetClass, setSelectedAssetClass] = useState(null);
  const [marketModalClass, setMarketModalClass] = useState(null);
  const [fromTemplateOpen, setFromTemplateOpen] = useState(false);
  const [templatePick, setTemplatePick] = useState('');
  const [templateDisplayName, setTemplateDisplayName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [deployModalClass, setDeployModalClass] = useState(null);
  const [custodyVaultDeploy, setCustodyVaultDeploy] = useState(null);
  const [fractionTokenDeploy, setFractionTokenDeploy] = useState(null);

  const filteredAssetClasses = useMemo(
    () => filterAssetClasses(assetClasses, { searchQuery, sectorFilter }),
    [assetClasses, searchQuery, sectorFilter]
  );

  const templatesList = useMemo(
    () =>
      Object.values(assetClassTemplates || {}).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      ),
    [assetClassTemplates]
  );

  const refresh = useCallback(async () => {
    await loadAssetClasses();
  }, [loadAssetClasses]);

  useEffect(() => {
    if (isAdmin) {
      refresh();
    }
  }, [isAdmin, refresh]);

  const openModal = (modalType, assetClass = null) => {
    setSelectedAssetClass(assetClass);
    setActiveModal(modalType);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedAssetClass(null);
  };

  const submitFromTemplate = async () => {
    if (!templatePick) {
      toast.error('Select a template');
      return;
    }
    try {
      await createFromTemplate(
        templatePick,
        { name: templateDisplayName.trim() || undefined },
        { platformStandard: true }
      );
      setFromTemplateOpen(false);
      setTemplatePick('');
      setTemplateDisplayName('');
      await refresh();
    } catch {
      /* createFromTemplate / createAssetClass already toast */
    }
  };

  if (!isAdmin) {
    return (
      <ManagerContainer>
        <Back to="/">
          <FiArrowLeft /> Back to dashboard
        </Back>
        <Gate>
          <h2>Admin only</h2>
          <p>Sign in as an administrator to create standard asset classes.</p>
        </Gate>
      </ManagerContainer>
    );
  }

  return (
    <ManagerContainer>
      <Back to="/">
        <FiArrowLeft /> Back to dashboard
      </Back>

      <HeroSection>
        <HeroTitle>
          <FiDatabase /> Standard asset classes
        </HeroTitle>
        <HeroSubtitle>
          Create asset classes from templates or custom definitions. Each class is stored in the shared catalog and
          appears for <strong>authorized tokenizers</strong> on <strong>Asset Class Manager → Active Asset Classes</strong>{' '}
          with a <strong>Standard asset class</strong> label.
        </HeroSubtitle>
      </HeroSection>

      <AssetClassesSection>
        <ActionsSection>
          <SectionTitle>
            <FiTarget className="icon" />
            Active Asset Classes
          </SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <ActionButton type="button" primary onClick={() => setFromTemplateOpen(true)}>
              <FiCopy /> Create from template
            </ActionButton>
            <ActionButton type="button" onClick={() => openModal('createCustom')}>
              <FiPlus /> Create without template
            </ActionButton>
            <ActionButton type="button" onClick={refresh} disabled={loading}>
              <FiRefreshCw /> Refresh
            </ActionButton>
          </div>
        </ActionsSection>

        <FilterBar>
          <FilterSearchWrap>
            <FiSearch aria-hidden />
            <FilterTextInput
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, code, sector, symbol…"
              aria-label="Search asset classes"
            />
          </FilterSearchWrap>
          <FilterSectorWrap>
            <label htmlFor="admin-asset-class-sector-filter">Sector</label>
            <FilterSelect
              id="admin-asset-class-sector-filter"
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
            >
              <option value="">All sectors</option>
              {TOKENIZER_PLATFORM_SECTORS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </FilterSelect>
          </FilterSectorWrap>
          {assetClasses.length > 0 && (
            <FilterMeta>
              Showing {filteredAssetClasses.length} of {assetClasses.length}
            </FilterMeta>
          )}
        </FilterBar>

        {assetClasses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#a0a0a0' }}>
            <FiLayers style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
            <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>No Asset Classes Yet</h3>
            <p>Create a standard class from a template or build a custom one above.</p>
          </div>
        ) : filteredAssetClasses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#a0a0a0' }}>
            <FiSearch style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
            <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>No matching asset classes</h3>
            <p>Try a different search or set Sector to “All sectors”.</p>
          </div>
        ) : (
          <AssetClassGrid>
            {filteredAssetClasses.map((assetClass, index) => {
              const canAddBatch = hasAssetClassDeployedContract(assetClass);
              return (
              <AssetClassCard
                key={assetClass.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="class-header">
                  <div className="class-info">
                    <div className="class-name">{assetClass.name}</div>
                    <div className="class-details">
                      {assetClass.marketSymbol} • {assetClass.tradingUnit} • {assetClass.classType}
                    </div>
                    {assetClass.isPlatformStandard && (
                      <span className="badge-standard">Standard asset class</span>
                    )}
                  </div>
                </div>

                <div className="class-metrics">
                  <div className="metric">
                    <div className="metric-value">{assetClass.totalBatches}</div>
                    <div className="metric-label">Batches</div>
                  </div>
                  <div className="metric">
                    <div className="metric-value">{assetClass.totalSupply}</div>
                    <div className="metric-label">Total Supply</div>
                  </div>
                  <div className="metric">
                    <div className="metric-value">
                      {assetClass.isActive ? <FiCheck style={{ color: '#00ff88' }} /> : <FiClock />}
                    </div>
                    <div className="metric-label">Status</div>
                  </div>
                </div>

                <AssetClassContractCardBlock
                  assetClass={assetClass}
                  actionClassName="class-action"
                  onDeployClick={() => setDeployModalClass(assetClass)}
                />

                {assetClass.tokenStandard === 'ERC721' && canAddBatch && (
                  <AssetClassCustodyVaultBlock
                    assetClass={assetClass}
                    isAdmin
                    tokenPickerOnCard={false}
                    actionClassName="class-action"
                    onOpenCustodyVaultModal={() =>
                      setCustodyVaultDeploy({ assetClass, adminCustodyOverview: true })
                    }
                  />
                )}

                {assetClass.tokenStandard === 'ERC721' && canAddBatch && (
                  <AssetClassFractionTokenBlock
                    assetClass={assetClass}
                    isAdmin
                    tokenPickerOnCard={false}
                    actionClassName="class-action"
                    onOpenFractionTokenModal={() =>
                      setFractionTokenDeploy({ assetClass, adminFractionOverview: true })
                    }
                  />
                )}

                <div className="class-actions">
                  <button
                    type="button"
                    className="class-action primary"
                    disabled={!canAddBatch}
                    title={
                      canAddBatch
                        ? undefined
                        : 'Deploy the smart contract for this asset class before adding batches.'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canAddBatch) return;
                      openModal('addBatch', assetClass);
                    }}
                  >
                    <FiPlus />
                    Add Batch
                  </button>
                  <button
                    type="button"
                    className="class-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal('viewDetails', assetClass);
                    }}
                  >
                    <FiEye />
                    View
                  </button>
                  <button
                    type="button"
                    className="class-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMarketModalClass(assetClass);
                    }}
                  >
                    <FiBarChart />
                    Market
                  </button>
                </div>
              </AssetClassCard>
            );
            })}
          </AssetClassGrid>
        )}
      </AssetClassesSection>

      <AnimatePresence>
        {deployModalClass && (
          <AssetClassDeployContractModal
            assetClass={deployModalClass}
            onClose={() => setDeployModalClass(null)}
            onCompleted={() => loadAssetClasses()}
          />
        )}
        {custodyVaultDeploy && (
          <AssetClassCustodyVaultDeployModal
            assetClass={
              assetClasses.find((c) => c.id === custodyVaultDeploy.assetClass.id) ?? custodyVaultDeploy.assetClass
            }
            authorizedTokenizerId={custodyVaultDeploy.authorizedTokenizerId ?? null}
            adminCustodyOverview={custodyVaultDeploy.adminCustodyOverview === true}
            onClose={() => setCustodyVaultDeploy(null)}
            onCompleted={() => loadAssetClasses()}
          />
        )}
        {fractionTokenDeploy && (
          <AssetClassFractionTokenDeployModal
            assetClass={
              assetClasses.find((c) => c.id === fractionTokenDeploy.assetClass.id) ?? fractionTokenDeploy.assetClass
            }
            authorizedTokenizerId={fractionTokenDeploy.authorizedTokenizerId ?? null}
            adminFractionOverview={fractionTokenDeploy.adminFractionOverview === true}
            onClose={() => setFractionTokenDeploy(null)}
            onCompleted={() => loadAssetClasses()}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {fromTemplateOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setFromTemplateOpen(false)}
          >
            <FormModalSurface onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '1rem', color: '#fff' }}>Create standard class from template</h3>
              <Field>
                <span>Template</span>
                <select value={templatePick} onChange={(e) => setTemplatePick(e.target.value)}>
                  <option value="">Select…</option>
                  {templatesList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} — {labelTokenizerPlatformCategoryId(t.category)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <span>Display name (optional)</span>
                <input
                  value={templateDisplayName}
                  onChange={(e) => setTemplateDisplayName(e.target.value)}
                  placeholder="Defaults to template name"
                />
              </Field>
              <ToolbarRow>
                <ActionButton type="button" primary onClick={submitFromTemplate} disabled={!templatePick}>
                  <FiCopy /> Create class
                </ActionButton>
                <ActionButton type="button" onClick={() => setFromTemplateOpen(false)}>
                  Cancel
                </ActionButton>
              </ToolbarRow>
            </FormModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeModal && activeModal !== 'viewDetails' && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {activeModal === 'createCustom' && (
                <AssetClassCreationWizard onClose={closeModal} platformStandard />
              )}
              {activeModal === 'addBatch' && (
                <BatchMintingWizard assetClass={selectedAssetClass} onClose={closeModal} />
              )}
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeModal === 'viewDetails' && selectedAssetClass && (
          <AssetClassDetailsModal assetClass={selectedAssetClass} onClose={closeModal} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {marketModalClass && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMarketModalClass(null)}
          >
            <SchemaViewModalSurface
              initial={{ scale: 0.92, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              <SchemaModalHeader>
                <div>
                  <h2>Spot market</h2>
                  <div className="meta">
                    {marketModalClass.name} · {marketModalClass.marketSymbol} ·{' '}
                    {marketModalClass.tradingUnit || 'unit'}
                  </div>
                </div>
                <ActionButton type="button" onClick={() => setMarketModalClass(null)}>
                  Close
                </ActionButton>
              </SchemaModalHeader>
              <div style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: 1.6 }}>
                <p style={{ margin: '0 0 1rem', color: '#94a3b8' }}>
                  Listing symbol used for spot-style trading and batch economics for this class.
                </p>
                <div style={{ display: 'grid', gap: '0.65rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Quote style</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>
                      {marketModalClass.marketSymbol} / USD
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Trading unit</span>
                    <span style={{ color: '#fff' }}>{marketModalClass.tradingUnit || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Token standard</span>
                    <span style={{ color: '#fff' }}>{marketModalClass.tokenStandard || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Batches</span>
                    <span style={{ color: '#fff' }}>{marketModalClass.totalBatches ?? 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ color: '#94a3b8' }}>Class status</span>
                    <span style={{ color: marketModalClass.isActive ? '#4ade80' : '#fbbf24' }}>
                      {marketModalClass.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {(() => {
                    const pair = getMarketPairForAssetClass(marketModalClass.id);
                    if (!pair) {
                      return (
                        <p style={{ margin: '1rem 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                          No separate market-pair record is registered in the app for this class yet; the symbol above
                          still identifies how this class is quoted alongside others.
                        </p>
                      );
                    }
                    return (
                      <div
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.75rem 1rem',
                          background: 'rgba(79, 172, 254, 0.08)',
                          borderRadius: '8px',
                          border: '1px solid rgba(79, 172, 254, 0.25)'
                        }}
                      >
                        <div style={{ fontSize: '0.8rem', color: '#7dd3fc', marginBottom: '0.35rem' }}>
                          Registered pair
                        </div>
                        <pre style={{ margin: 0, fontSize: '0.78rem', color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(pair, null, 2)}
                        </pre>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </SchemaViewModalSurface>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </ManagerContainer>
  );
}

export default AdminAssetClasses;
